import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Bell, Send, Clock, Users, Eye, BellOff, Search, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { sq } from 'date-fns/locale';
import toast from 'react-hot-toast';
import { notificationsApi, type NotificationsParams, type SubscriberItem } from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import Badge from '../components/Badge';
import type { BadgeVariant } from '../components/Badge';

const SECTION_LABELS: Record<string, string> = {
  POSTIME: 'Postime',
  LAJME: 'Lajme',
  NARRATIVA: 'Narrativa',
  PS: 'PS',
  GALERIA: 'Galeria',
  KUVEND: 'Kuvend',
  QEVERI: 'Qeveri',
};

const SECTION_COLORS: Record<string, BadgeVariant> = {
  POSTIME: 'primary',
  LAJME: 'info',
  NARRATIVA: 'purple',
  PS: 'danger',
  GALERIA: 'warning',
  KUVEND: 'success',
  QEVERI: 'default',
};

export default function NotificationsPage() {
  const queryClient = useQueryClient();

  // Send form state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [postId, setPostId] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // History state
  const [historyPage, setHistoryPage] = useState(1);
  const historyLimit = 10;

  // Subscribers state
  const [subscriberSearch, setSubscriberSearch] = useState('');

  // Send to user state
  const [sendToUserTarget, setSendToUserTarget] = useState<SubscriberItem | null>(null);
  const [userMsgTitle, setUserMsgTitle] = useState('');
  const [userMsgBody, setUserMsgBody] = useState('');
  const [userMsgErrors, setUserMsgErrors] = useState<Record<string, string>>({});

  const historyParams: NotificationsParams = {
    page: historyPage,
    limit: historyLimit,
  };

  const historyQuery = useQuery({
    queryKey: ['notifications-history', historyParams],
    queryFn: () => notificationsApi.getAll(historyParams),
  });

  const subscribersQuery = useQuery({
    queryKey: ['notification-subscribers'],
    queryFn: () => notificationsApi.getSubscribers(),
  });

  const sendToUserMutation = useMutation({
    mutationFn: () =>
      notificationsApi.sendToUser(sendToUserTarget!.id, userMsgTitle, userMsgBody),
    onSuccess: () => {
      toast.success(`Njoftimi u dergua te ${sendToUserTarget?.firstName} ${sendToUserTarget?.lastName}`);
      queryClient.invalidateQueries({ queryKey: ['notifications-history'] });
      setSendToUserTarget(null);
      setUserMsgTitle('');
      setUserMsgBody('');
      setUserMsgErrors({});
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi dergimi');
    },
  });

  const sendMutation = useMutation({
    mutationFn: () =>
      notificationsApi.sendUrgent(title, body, postId || undefined),
    onSuccess: (data) => {
      toast.success(`Njoftimi u dergua me sukses te ${data.deliveryCount} perdorues`);
      queryClient.invalidateQueries({ queryKey: ['notifications-history'] });
      setTitle('');
      setBody('');
      setPostId('');
      setShowConfirm(false);
    },
    onError: (err: Error) => {
      toast.error(err.message || 'Deshtoi dergimi i njoftimit');
      setShowConfirm(false);
    },
  });

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!title.trim()) {
      errors.title = 'Titulli eshte i detyrueshem';
    }
    if (title.length > 100) {
      errors.title = 'Titulli nuk mund te jete me i gjate se 100 karaktere';
    }
    if (!body.trim()) {
      errors.body = 'Permbajtja eshte e detyrueshem';
    }
    if (body.length > 500) {
      errors.body = 'Permbajtja nuk mund te jete me e gjate se 500 karaktere';
    }
    if (postId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(postId)) {
      errors.postId = 'ID e postimit duhet te jete nje UUID e vlefshme';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validateForm()) return;
    setShowConfirm(true);
  }

  function handleSendToUser(e: FormEvent) {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!userMsgTitle.trim()) errors.title = 'Titulli eshte i detyrueshem';
    if (!userMsgBody.trim()) errors.body = 'Permbajtja eshte e detyrueshem';
    setUserMsgErrors(errors);
    if (Object.keys(errors).length > 0) return;
    sendToUserMutation.mutate();
  }

  const history = historyQuery.data?.data ?? [];
  const historyPagination = historyQuery.data?.pagination;

  const subscribers = subscribersQuery.data ?? [];
  const filteredSubscribers = subscriberSearch
    ? subscribers.filter(
        (s) =>
          s.firstName.toLowerCase().includes(subscriberSearch.toLowerCase()) ||
          s.lastName.toLowerCase().includes(subscriberSearch.toLowerCase()) ||
          s.email.toLowerCase().includes(subscriberSearch.toLowerCase()),
      )
    : subscribers;

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Njoftimet</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Send Notification Form */}
        <div>
          <div className="card">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <Bell className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Dergo Njoftim Urgjent</h2>
                <p className="text-xs text-gray-400">
                  Njoftimi do te dergohet te te gjithe perdoruesit
                </p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titulli *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setFormErrors((p) => ({ ...p, title: '' }));
                  }}
                  className="input-field"
                  placeholder="p.sh. Njoftim i Rendesishem"
                  maxLength={100}
                />
                {formErrors.title && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>
                )}
                <p className="text-xs text-gray-400 mt-1 text-right">{title.length}/100</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Permbajtja *
                </label>
                <textarea
                  value={body}
                  onChange={(e) => {
                    setBody(e.target.value);
                    setFormErrors((p) => ({ ...p, body: '' }));
                  }}
                  className="input-field min-h-[120px] resize-y"
                  placeholder="Shkruani permbajtjen e njoftimit..."
                  maxLength={500}
                  rows={4}
                />
                {formErrors.body && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.body}</p>
                )}
                <p className="text-xs text-gray-400 mt-1 text-right">{body.length}/500</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ID e Postimit (opsionale)
                </label>
                <input
                  type="text"
                  value={postId}
                  onChange={(e) => {
                    setPostId(e.target.value);
                    setFormErrors((p) => ({ ...p, postId: '' }));
                  }}
                  className="input-field"
                  placeholder="UUID e postimit te lidhur"
                />
                {formErrors.postId && (
                  <p className="text-xs text-red-500 mt-1">{formErrors.postId}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Lidhni njoftimin me nje postim specifik
                </p>
              </div>

              <button
                type="submit"
                disabled={sendMutation.isPending}
                className="btn-danger w-full flex items-center justify-center gap-2 py-2.5"
              >
                <Send className="h-4 w-4" />
                <span>Dergo Njoftimin Urgjent</span>
              </button>
            </form>
          </div>

          {/* Preview */}
          {(title.trim() || body.trim()) && (
            <div className="card mt-4">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="h-4 w-4 text-gray-400" />
                <h3 className="text-sm font-medium text-gray-700">Parashikim</h3>
              </div>
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0 mt-0.5">
                    <Bell className="h-5 w-5 text-red-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="danger">URGJENT</Badge>
                      <span className="text-[10px] text-gray-400">Tani</span>
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 mt-1">
                      {title || 'Titulli i njoftimit'}
                    </h4>
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">
                      {body || 'Permbajtja e njoftimit...'}
                    </p>
                    {postId && (
                      <p className="text-[10px] text-barrikadave-500 mt-2">
                        Lidhur me postimin: {postId.slice(0, 8)}...
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* History */}
        <div>
          <div className="card !p-0 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-gray-400" />
                <h2 className="text-lg font-semibold text-gray-900">Historia e Njoftimeve</h2>
              </div>
            </div>

            {historyQuery.isLoading ? (
              <LoadingSpinner className="py-16" />
            ) : historyQuery.isError ? (
              <div className="text-center py-16 px-6">
                <p className="text-red-500 text-sm">Gabim gjate ngarkimit.</p>
                <button
                  onClick={() => historyQuery.refetch()}
                  className="btn-secondary mt-3 text-sm"
                >
                  Provo perseri
                </button>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-16 px-6">
                <Bell className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">Nuk ka njoftime te derguar.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Titulli
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Tipi
                      </th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Derguar
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {history.map((notif) => {
                      const notifDate = format(
                        new Date(notif.createdAt),
                        'dd MMM yyyy, HH:mm',
                        { locale: sq },
                      );

                      const typeVariant =
                        notif.type === 'URGENT_ALERT'
                          ? 'danger'
                          : notif.type === 'SYSTEM'
                            ? 'primary'
                            : 'default';

                      return (
                        <tr key={notif.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                            {notifDate}
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                              {notif.title}
                            </p>
                            <p className="text-xs text-gray-400 truncate max-w-[200px]">
                              {notif.body}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={typeVariant}>{notif.type}</Badge>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1 text-sm text-gray-600">
                              <Users className="h-3.5 w-3.5 text-gray-400" />
                              <span>{notif.deliveryCount}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {historyPagination && historyPagination.totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50">
                <Pagination
                  page={historyPagination.page}
                  totalPages={historyPagination.totalPages}
                  onPageChange={setHistoryPage}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Subscribers Section */}
      <div className="card !p-0 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-barrikadave-500" />
              <h2 className="text-lg font-semibold text-gray-900">Abonimet e Perdoruesve</h2>
              {subscribers.length > 0 && (
                <span className="text-xs text-gray-400">({subscribers.length} perdorues)</span>
              )}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={subscriberSearch}
                onChange={(e) => setSubscriberSearch(e.target.value)}
                placeholder="Kerko perdorues..."
                className="input-field pl-9 !py-1.5 text-sm"
              />
            </div>
          </div>
        </div>

        {subscribersQuery.isLoading ? (
          <LoadingSpinner className="py-16" />
        ) : subscribersQuery.isError ? (
          <div className="text-center py-16 px-6">
            <p className="text-red-500 text-sm">Gabim gjate ngarkimit.</p>
            <button
              onClick={() => subscribersQuery.refetch()}
              className="btn-secondary mt-3 text-sm"
            >
              Provo perseri
            </button>
          </div>
        ) : filteredSubscribers.length === 0 ? (
          <div className="text-center py-16 px-6">
            <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">
              {subscriberSearch ? 'Nuk u gjeten perdorues.' : 'Nuk ka perdorues te abonuar.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Perdoruesi
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Kategorite e abonuara
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Veprime
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSubscribers.map((sub) => (
                  <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-barrikadave-100 flex items-center justify-center shrink-0">
                          <span className="text-barrikadave-600 font-semibold text-xs">
                            {sub.firstName[0]}{sub.lastName[0]}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {sub.firstName} {sub.lastName}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {sub.email}
                    </td>
                    <td className="px-4 py-3">
                      {!sub.notificationsEnabled ? (
                        <div className="flex items-center gap-1.5 text-gray-400">
                          <BellOff className="h-3.5 w-3.5" />
                          <span className="text-xs">Njoftimet te c'aktivizuara</span>
                        </div>
                      ) : sub.subscribedSections.length === 0 ? (
                        <span className="text-xs text-gray-400">Asnje kategori</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {sub.subscribedSections.map((section) => (
                            <Badge
                              key={section}
                              variant={SECTION_COLORS[section] || 'default'}
                            >
                              {SECTION_LABELS[section] || section}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => {
                          setSendToUserTarget(sub);
                          setUserMsgTitle('');
                          setUserMsgBody('');
                          setUserMsgErrors({});
                        }}
                        title="Dergo njoftim"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-barrikadave-600 hover:bg-barrikadave-50 transition-colors"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Send to User Modal */}
      <Modal
        isOpen={!!sendToUserTarget}
        onClose={() => {
          setSendToUserTarget(null);
          setUserMsgTitle('');
          setUserMsgBody('');
          setUserMsgErrors({});
        }}
        title={`Dergo Njoftim: ${sendToUserTarget?.firstName} ${sendToUserTarget?.lastName}`}
        size="lg"
      >
        <form onSubmit={handleSendToUser} className="space-y-4">
          <p className="text-sm text-gray-500">
            Njoftimi do te dergohet vetem te <strong>{sendToUserTarget?.email}</strong>
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titulli *</label>
            <input
              type="text"
              value={userMsgTitle}
              onChange={(e) => {
                setUserMsgTitle(e.target.value);
                setUserMsgErrors((p) => ({ ...p, title: '' }));
              }}
              className="input-field"
              placeholder="Titulli i njoftimit"
              maxLength={100}
            />
            {userMsgErrors.title && (
              <p className="text-xs text-red-500 mt-1">{userMsgErrors.title}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Permbajtja *</label>
            <textarea
              value={userMsgBody}
              onChange={(e) => {
                setUserMsgBody(e.target.value);
                setUserMsgErrors((p) => ({ ...p, body: '' }));
              }}
              className="input-field min-h-[100px] resize-y"
              placeholder="Shkruani permbajtjen..."
              maxLength={500}
              rows={3}
            />
            {userMsgErrors.body && (
              <p className="text-xs text-red-500 mt-1">{userMsgErrors.body}</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setSendToUserTarget(null)}
              className="btn-ghost"
            >
              Anulo
            </button>
            <button
              type="submit"
              disabled={sendToUserMutation.isPending}
              className="btn-primary flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {sendToUserMutation.isPending ? 'Duke derguar...' : 'Dergo'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Send Confirm Dialog */}
      <ConfirmDialog
        isOpen={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={() => sendMutation.mutate()}
        title="Konfirmo Dergimin"
        message={`Jeni te sigurt qe deshironi te dergoni njoftimin urgjent "${title}" te te gjithe perdoruesit? Ky veprim nuk mund te kthehet mbrapsht.`}
        confirmText="Dergo"
        variant="danger"
        isLoading={sendMutation.isPending}
      />
    </div>
  );
}
